import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal, MaskLineInView } from "@/components/motion/Reveal";
import { BeforeAfter } from "@/components/portfolio/BeforeAfter";
import { PORTFOLIO, PORTFOLIO_TABS, IMAGES } from "@/data/site";

const Portfolio = () => {
  const [tab, setTab] = useState("All");
  const filtered = tab === "All" ? PORTFOLIO : PORTFOLIO.filter((p) => p.category === tab);

  return (
    <div data-testid="portfolio-page" className="bg-[#FAF9F6] pt-20">
      {/* Header */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pt-20 md:pt-32 pb-12 md:pb-16">
        <Reveal>
          <div className="overline text-[#8B7355] mb-8">Selected Work</div>
        </Reveal>
        <h1 className="font-serif-display text-[#1A1A1A] leading-[0.98] text-[clamp(2.5rem,7vw,6.5rem)] tracking-[-0.02em]">
          <MaskLineInView>The Collection.</MaskLineInView>
        </h1>
        <Reveal delay={0.15}>
          <p className="mt-8 text-[#4A4A4A] text-base md:text-lg max-w-xl leading-relaxed">
            A curated portfolio of turnkey residences across Noida &amp; Delhi NCR &mdash; each one engineered for beauty, permanence and premium valuation.
          </p>
        </Reveal>

        {/* Tabs */}
        <Reveal delay={0.2}>
          <div className="mt-12 flex flex-wrap gap-3">
            {PORTFOLIO_TABS.map((t) => (
              <button
                key={t}
                data-testid={`portfolio-tab-${t.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setTab(t)}
                className={`px-5 py-2.5 text-sm tracking-wide border transition-colors duration-300 ${
                  tab === t
                    ? "bg-[#1A1A1A] text-[#FAF9F6] border-[#1A1A1A]"
                    : "bg-transparent text-[#4A4A4A] border-[#E5E5E5] hover:border-[#C5A880] hover:text-[#1A1A1A]"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </Reveal>
      </section>

      {/* Grid */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-24 md:pb-32">
        <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-7">
          <AnimatePresence mode="popLayout">
            {filtered.map((p, i) => (
              <motion.div
                layout
                key={p.id}
                data-testid={`portfolio-card-${p.id}`}
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.98 }}
                transition={{ duration: 0.6, delay: (i % 3) * 0.06, ease: [0.22, 1, 0.36, 1] }}
                className="group cursor-pointer"
              >
                <div className="relative overflow-hidden bg-[#1A1A1A]">
                  <img
                    src={p.image}
                    alt={p.name}
                    loading="lazy"
                    className="w-full h-[300px] md:h-[380px] object-cover group-hover:scale-105 transition-transform duration-[900ms]"
                  />
                  {/* Hover caption overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/90 via-[#1A1A1A]/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="absolute bottom-0 left-0 right-0 p-6 translate-y-3 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-[transform,opacity] duration-500">
                    <div className="overline text-[#C5A880] mb-2">{p.theme}</div>
                    <h3 className="font-serif-display text-xl md:text-2xl text-[#FAF9F6] leading-tight">{p.name}</h3>
                    <p className="text-[#FAF9F6]/70 text-xs mt-1.5">{p.subtitle} &middot; {p.location}</p>
                  </div>
                  <span className="absolute top-4 right-4 w-9 h-9 rounded-full bg-[#FAF9F6]/95 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    <ArrowUpRight size={16} className="text-[#1A1A1A]" />
                  </span>
                </div>
                {/* Static caption below image */}
                <div className="mt-4">
                  <div className="overline text-[#8B7355] mb-1.5">{p.category}</div>
                  <h3 className="font-serif-display text-lg md:text-xl text-[#1A1A1A] leading-snug">{p.name}</h3>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      </section>

      {/* Before / After */}
      <section className="bg-[#F2EFEB] border-y border-[#E5E5E5]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-36">
          <div className="grid md:grid-cols-12 gap-10 items-center mb-12">
            <div className="md:col-span-5">
              <Reveal>
                <div className="overline text-[#8B7355] mb-6">The Transformation</div>
                <h2 className="font-serif-display text-4xl md:text-5xl leading-[1.05] text-[#1A1A1A]">
                  From bare shell to <span className="italic text-[#C5A880]">bespoke haven.</span>
                </h2>
                <p className="mt-6 text-[#4A4A4A] leading-relaxed max-w-md">
                  Drag to reveal the before-and-after of a Sector 150 living space &mdash; a study in spatial engineering and material restraint.
                </p>
              </Reveal>
            </div>
            <div className="md:col-span-7">
              <Reveal delay={0.15}>
                <BeforeAfter
                  before={IMAGES.philosophyB}
                  after={PORTFOLIO[3].image}
                  beforeLabel="Bare Shell"
                  afterLabel="Curated"
                />
              </Reveal>
            </div>
          </div>

          <Reveal>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 pt-8 border-t border-[#E5E5E5]">
              <p className="font-serif-display text-2xl md:text-3xl text-[#1A1A1A] max-w-xl">Your residence could be next.</p>
              <Link
                to="/consultation"
                data-testid="portfolio-cta"
                className="group inline-flex items-center gap-4 bg-[#1A1A1A] text-[#FAF9F6] px-8 py-4 hover:bg-[#C5A880] hover:text-[#1A1A1A] transition-colors duration-400 shrink-0 w-fit"
              >
                <span className="text-sm tracking-wide">Start Your Project</span>
                <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Portfolio;
